// Subscription Service for Triyo Premium
// Handles subscription status, usage tracking, and checkout URL generation

import { supabase } from "./supabase";

// Types
export interface Subscription {
    id: string;
    userId: string;
    lemonCustomerId: string | null;
    lemonSubscriptionId: string | null;
    status: "free" | "active" | "cancelled" | "expired" | "past_due";
    planType: "monthly" | "annual" | null;
    currentPeriodEnd: string | null;
}

export interface UsageQuota {
    used: number;
    limit: number;
    remaining: number;
    unlimited: boolean;
}

export interface DailyUsage {
    aiTips: UsageQuota;
    smartTasks: UsageQuota;
}

// Free tier limits
const FREE_LIMITS = {
    ai_tips: 3,
    smart_tasks: 1,
} as const;

// Lemon Squeezy variant IDs (from env)
const MONTHLY_VARIANT_ID = import.meta.env.VITE_LEMON_MONTHLY_VARIANT_ID || "";
const ANNUAL_VARIANT_ID = import.meta.env.VITE_LEMON_ANNUAL_VARIANT_ID || "";
const STORE_ID = import.meta.env.VITE_LEMON_STORE_ID || "";

/**
 * Get user's subscription status
 */
export async function getSubscription(userId: string): Promise<Subscription | null> {
    const { data, error } = await supabase
        .from("subscriptions")
        .select("*")
        .eq("user_id", userId)
        .maybeSingle();

    if (error || !data) {
        return null;
    }

    return {
        id: data.id,
        userId: data.user_id,
        lemonCustomerId: data.lemon_customer_id,
        lemonSubscriptionId: data.lemon_subscription_id,
        status: data.status,
        planType: data.plan_type,
        currentPeriodEnd: data.current_period_end,
    };
}

/**
 * Check if user has active premium subscription
 */
export async function isUserPremium(userId: string): Promise<boolean> {
    const { data, error } = await supabase
        .rpc("is_user_premium", { target_user_id: userId });

    if (error) {
        console.error("Error checking premium status:", error);
        return false;
    }

    return data === true;
}

/**
 * Get user's daily usage limits
 */
export async function getDailyUsage(userId: string): Promise<DailyUsage> {
    const isPremium = await isUserPremium(userId);

    if (isPremium) {
        return {
            aiTips: { used: 0, limit: Infinity, remaining: Infinity, unlimited: true },
            smartTasks: { used: 0, limit: Infinity, remaining: Infinity, unlimited: true },
        };
    }

    const { data, error } = await supabase
        .rpc("get_daily_usage", { target_user_id: userId });

    if (error || !data || data.length === 0) {
        // No usage record yet = fresh quota
        return {
            aiTips: {
                used: 0,
                limit: FREE_LIMITS.ai_tips,
                remaining: FREE_LIMITS.ai_tips,
                unlimited: false
            },
            smartTasks: {
                used: 0,
                limit: FREE_LIMITS.smart_tasks,
                remaining: FREE_LIMITS.smart_tasks,
                unlimited: false
            },
        };
    }

    const usage = data[0];
    return {
        aiTips: {
            used: usage.ai_tips_used,
            limit: FREE_LIMITS.ai_tips,
            remaining: Math.max(0, FREE_LIMITS.ai_tips - usage.ai_tips_used),
            unlimited: false,
        },
        smartTasks: {
            used: usage.smart_tasks_used,
            limit: FREE_LIMITS.smart_tasks,
            remaining: Math.max(0, FREE_LIMITS.smart_tasks - usage.smart_tasks_used),
            unlimited: false,
        },
    };
}

/**
 * Track usage of a feature (increment counter)
 * Returns updated quota and whether limit was exceeded
 */
export async function trackUsage(
    userId: string,
    feature: "ai_tips" | "smart_tasks"
): Promise<{ success: boolean; limitReached: boolean; quota: UsageQuota }> {
    // Check if premium first
    const isPremium = await isUserPremium(userId);
    if (isPremium) {
        return {
            success: true,
            limitReached: false,
            quota: { used: 0, limit: Infinity, remaining: Infinity, unlimited: true },
        };
    }

    // Increment usage via RPC
    const { data, error } = await supabase
        .rpc("increment_usage", {
            target_user_id: userId,
            feature
        });

    if (error) {
        console.error("Error tracking usage:", error);
        return {
            success: false,
            limitReached: false,
            quota: { used: 0, limit: FREE_LIMITS[feature], remaining: FREE_LIMITS[feature], unlimited: false },
        };
    }

    const result = data?.[0];
    const limit = FREE_LIMITS[feature];
    const used = result?.new_count || 0;

    return {
        success: true,
        limitReached: result?.limit_reached || false,
        quota: {
            used,
            limit,
            remaining: Math.max(0, limit - used),
            unlimited: false,
        },
    };
}

/**
 * Check if user can use a feature (without incrementing)
 */
export async function canUseFeature(
    userId: string,
    feature: "ai_tips" | "smart_tasks"
): Promise<boolean> {
    const usage = await getDailyUsage(userId);
    const quota = feature === "ai_tips" ? usage.aiTips : usage.smartTasks;
    return quota.unlimited || quota.remaining > 0;
}

/**
 * Generate Lemon Squeezy checkout URL
 */
export function generateCheckoutUrl(
    userId: string,
    plan: "monthly" | "annual"
): string {
    const variantId = plan === "annual" ? ANNUAL_VARIANT_ID : MONTHLY_VARIANT_ID;

    if (!STORE_ID || !variantId) {
        console.error("Missing Lemon Squeezy configuration", { STORE_ID, variantId });
        return "#";
    }

    // Build checkout URL with custom data (passes user ID for webhook)
    const params = new URLSearchParams({
        "checkout[custom][user_id]": userId,
    });

    return `https://${STORE_ID}.lemonsqueezy.com/checkout/buy/${variantId}?${params.toString()}`;
}

/**
 * Get customer portal URL for managing subscription
 */
export function getCustomerPortalUrl(customerId: string): string {
    if (!STORE_ID) {
        return "#";
    }
    return `https://${STORE_ID}.lemonsqueezy.com/billing`;
}
